import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { ComboBox } from "@/components/ui/combo-box";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TypographyH3 } from "@/components/ui/typography";
import { cn } from "@/lib/utils";
import { AlertTriangle } from "lucide-react";
import { type Dispatch, Fragment, type SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type ParseResult,
  parseAmount,
  parseCurrency,
  parseDate,
  parsePurpose,
} from "./import-parsing";

type MappingStepProps = {
  parsed: Array<Record<string, string>>;
  mapping: Record<string, string>;
  setMapping: Dispatch<SetStateAction<Record<string, string>>>;
  onNext: () => void;
  onBack: () => void;
};

export default function MappingStep({
  parsed,
  mapping,
  setMapping,
  onNext,
  onBack,
}: MappingStepProps) {
  const [showMissingColumnsAlert, setShowMissingColumnsAlert] = useState(false);
  const { t } = useTranslation();

  const columns = Object.keys(parsed?.[0] ?? {});
  const fields: Array<{
    key: string;
    label: string;
    parse?: (value: string) => ParseResult<any>;
    optional?: boolean;
    fallback?: string;
  }> = [
    { key: "amount", label: "Betrag", parse: parseAmount },
    { key: "bookingDate", label: "Buchungsdatum", parse: parseDate },
    { key: "counterpartyName", label: "Gegenpartei Name" },
    { key: "counterpartyIban", label: "Gegenpartei IBAN", optional: true },
    {
      key: "purpose",
      label: "Verwendungszweck",
      optional: true,
      parse: parsePurpose,
    },
    {
      key: "currency",
      label: "Währung",
      parse: parseCurrency,
      optional: true,
      fallback: "EUR",
    },
    {
      key: "originalAmount",
      label: "Ursprünglicher Betrag",
      parse: parseAmount,
      optional: true,
    },
    {
      key: "originalCurrency",
      label: "Ursprüngliche Währung",
      parse: parseCurrency,
      optional: true,
    },
  ];

  return (
    <div className="w-full overflow-auto">
      <TypographyH3 className="mb-2">Spaltenzuordnung</TypographyH3>
      <p className="text-sm text-gray-600 mb-4 font-medium">
        {t("transactions.file_import.guidance_mapping")}
      </p>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-40 truncate">Feld</TableHead>
            <TableHead className="w-80 truncate">CSV-Spalte</TableHead>
            <TableHead className="w-80 truncate">
              Beispielwerte aus der Datei
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {fields.map((field) => (
            <TableRow key={field.key}>
              <TableCell className="w-40 max-w-xs truncate">
                {field.label}
                {field.optional && (
                  <div className="text-xs text-gray-400 mt-1">Optional</div>
                )}
              </TableCell>
              <TableCell className="w-80 truncate">
                <ComboBox
                  options={[
                    { label: "Keine Auswahl", value: "empty" },
                    ...columns.map((c) => ({ label: c, value: c })),
                  ]}
                  value={mapping[field.key] ?? null}
                  onChange={(v) => {
                    setMapping((m) => {
                      const newMapping = { ...m } as Record<string, string>;
                      if (v === "empty" || v === null || v === "")
                        delete newMapping[field.key];
                      else newMapping[field.key] = v;
                      return newMapping;
                    });
                  }}
                  placeholder={`Spalte auswählen${
                    field.optional ? " (optional)" : ""
                  }`}
                />
              </TableCell>
              <TableCell className="truncate">
                <MappingExamplesCell
                  rows={parsed}
                  columnKey={mapping[field.key]}
                  parser={field.parse}
                  fallback={field.fallback}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {showMissingColumnsAlert && (
        <Alert variant="destructive" className="mt-4">
          <AlertDescription>
            Alle benötigten Spalten müssen zugeordnet werden.
          </AlertDescription>
        </Alert>
      )}
      <div className="fixed bottom-0 left-0 right-0 flex justify-center w-screen bg-white">
        <div className="flex max-w-6xl justify-between w-full pl-6 pr-8 py-4">
          <Button variant="ghost" onClick={onBack}>
            Zurück
          </Button>
          <Button onClick={clickContinue}>Weiter</Button>
        </div>
      </div>
    </div>
  );

  function clickContinue() {
    if (!checkMissingColumns()) return;
    onNext();
  }

  function checkMissingColumns() {
    const requiredColumns = fields.filter((f) => !f.optional).map((f) => f.key);
    const missingColumns = requiredColumns.filter((c) => !mapping[c]);
    setShowMissingColumnsAlert(missingColumns.length > 0);
    return missingColumns.length === 0;
  }
}

function MappingExamplesCell({
  rows,
  columnKey,
  parser,
  fallback,
}: {
  rows: Array<Record<string, string>>;
  columnKey?: string;
  parser?: (value: string) => ParseResult<any>;
  fallback?: string;
}) {
  if (!columnKey) {
    if (fallback) {
      return (
        <div className="text-gray-400 px-1 rounded truncate">{fallback}</div>
      );
    }
    return <div />;
  }

  const rawValues = (rows ?? [])
    .map((r) => r?.[columnKey] ?? "")
    .filter((v) => String(v).trim() !== "");

  const examples: Array<{ text: string; error: boolean }> = [];
  const seen = new Set<string>();

  for (const raw of rawValues) {
    let text = String(raw);
    let error = false;
    if (parser) {
      const p = parser(raw);
      if (p?.value === undefined) {
        continue;
      }
      if (p?.error) {
        error = true;
        text = String(raw ?? "");
      } else {
        text = p?.userFormatted ? p.userFormatted : String(p.value);
      }
    }
    if (!seen.has(text)) {
      seen.add(text);
      examples.push({ text, error });
    }
    if (examples.length >= 3) break;
  }

  if (examples.length === 0 && fallback) {
    return (
      <div className="text-gray-400 px-1 w-[420px] rounded truncate">
        {fallback}
      </div>
    );
  }

  const hasError = examples.some((ex) => ex.error);

  return (
    <div className="flex items-center gap-2 w-[420px]">
      {hasError && (
        <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />
      )}
      <div>
        {examples.map((ex, idx) => (
          <Fragment key={idx}>
            <div
              className={cn(
                "inline-block rounded-lg bg-gray-100 px-3 py-1",
                ex.error ? "bg-red-200" : "bg-gray-100",
              )}
            >
              <span className="font-mono truncate">{ex.text}</span>
            </div>
            <span> {idx < examples.length - 1 ? ", " : ""}</span>
          </Fragment>
        ))}
      </div>
    </div>
  );
}
